import { useState, useEffect } from 'react';

interface SceneOutlineProps {
    content: string;
    onSceneClick: (line: number) => void;
}

interface OutlineItem {
    type: 'scene' | 'character';
    text: string;
    line: number;
}

export function SceneOutline({ content, onSceneClick }: SceneOutlineProps) {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [outline, setOutline] = useState<OutlineItem[]>([]);

    useEffect(() => {
        const lines = content.split('\n');
        const items: OutlineItem[] = [];

        lines.forEach((line, index) => {
            const trimmed = line.trim();

            // Scene heading detection
            if (/^(INT\.|EXT\.|INT\/EXT\.|I\/E\.)/i.test(trimmed)) {
                items.push({
                    type: 'scene',
                    text: trimmed,
                    line: index,
                });
            }
        });

        setOutline(items);
    }, [content]);

    if (isCollapsed) {
        return (
            <div
                style={{
                    width: '40px',
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderRight: '1px solid var(--color-border)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: 'var(--spacing-md) 0',
                }}
            >
                <button
                    onClick={() => setIsCollapsed(false)}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--color-text-secondary)',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        padding: 'var(--spacing-sm)',
                    }}
                    title="Expand outline"
                >
                    ☰
                </button>
            </div>
        );
    }

    return (
        <div
            style={{
                width: '280px',
                backgroundColor: 'var(--color-bg-secondary)',
                borderRight: '1px solid var(--color-border)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    padding: 'var(--spacing-md)',
                    borderBottom: '1px solid var(--color-border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                    Outline
                </h3>
                <button
                    onClick={() => setIsCollapsed(true)}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--color-text-secondary)',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        padding: 'var(--spacing-xs)',
                    }}
                    title="Collapse outline"
                >
                    ◀
                </button>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: 'var(--spacing-sm)' }}>
                {outline.length === 0 ? (
                    <div style={{ padding: 'var(--spacing-md)', color: 'var(--color-text-tertiary)', fontSize: '0.875rem', textAlign: 'center' }}>
                        No scenes found. Start writing!
                    </div>
                ) : (
                    <div className="flex flex-col gap-xs">
                        {outline.map((item, index) => (
                            <button
                                key={index}
                                onClick={() => onSceneClick(item.line)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 'var(--spacing-sm)',
                                    textAlign: 'left',
                                    color: 'var(--color-text-secondary)',
                                    fontSize: '0.8125rem',
                                    fontFamily: 'var(--font-mono)',
                                    cursor: 'pointer',
                                    borderRadius: 'var(--radius-sm)',
                                    transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <div style={{ fontWeight: 600, marginBottom: '2px' }}>
                                    Scene {index + 1}
                                </div>
                                <div style={{ color: 'var(--color-text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {item.text}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
